import React, { useState, useEffect } from 'react';
import { Mail, Save, RotateCcw, Send, CheckCircle, AlertCircle, Eye } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailTemplatesEditor() {
  const [templates, setTemplates] = useState({});
  const [activeTemplate, setActiveTemplate] = useState('welcome');
  const [editing, setEditing] = useState({ subject: '', html: '', name: '' });
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [message, setMessage] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => { fetchTemplates(); }, []);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/admin/email-templates`, { withCredentials: true });
      setTemplates(res.data.templates || {});
      const t = res.data.templates?.[activeTemplate];
      if (t) setEditing({ subject: t.subject, html: t.html, name: t.name });
    } catch {}
  };

  const selectTemplate = (tid) => {
    setActiveTemplate(tid);
    const t = templates[tid];
    if (t) setEditing({ subject: t.subject, html: t.html, name: t.name });
    setMessage(null);
    setShowPreview(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await axios.post(`${API}/admin/email-templates`, {
        template_id: activeTemplate, ...editing
      }, { withCredentials: true });
      setMessage({ type: 'success', text: 'Plantilla guardada' });
      fetchTemplates();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Error al guardar' });
    } finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!window.confirm('¿Restaurar plantilla a valores por defecto? Se perderán los cambios.')) return;
    try {
      await axios.post(`${API}/admin/email-templates/reset`, { template_id: activeTemplate }, { withCredentials: true });
      setMessage({ type: 'success', text: 'Plantilla restaurada' });
      fetchTemplates();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Error' });
    }
  };

  const handleSendTest = async () => {
    setSendingTest(true);
    setMessage(null);
    try {
      const res = await axios.post(`${API}/admin/email-templates/test`, { template_id: activeTemplate }, { withCredentials: true });
      setMessage({ type: 'success', text: res.data.message });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Error al enviar' });
    } finally { setSendingTest(false); }
  };

  return (
    <div className="space-y-6">
      {/* Template selector */}
      <div className="flex gap-2">
        {[
          { id: 'welcome', label: 'Bienvenida' },
          { id: 'renewal', label: 'Renovacion' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => selectTemplate(t.id)}
            data-testid={`email-template-${t.id}`}
            className={`px-5 py-2.5 text-sm font-bold uppercase tracking-wider transition-all duration-300 ${
              activeTemplate === t.id ? 'bg-diavel-red text-white' : 'bg-zinc-900 text-zinc-500 hover:text-white border border-white/5'
            }`}
          >
            <Mail className="w-4 h-4 inline mr-2" />{t.label}
          </button>
        ))}
      </div>

      {/* Editor */}
      <div className="bg-diavel-card border border-white/5 p-6">
        <div className="space-y-5">
          <div>
            <label className="font-mono text-xs tracking-wider uppercase text-zinc-500 block mb-2">Asunto del email</label>
            <input
              data-testid="email-subject-input"
              type="text" value={editing.subject}
              onChange={e => setEditing(p => ({ ...p, subject: e.target.value }))}
              className="w-full bg-zinc-900/50 border border-white/10 focus:border-red-600 focus:ring-1 focus:ring-red-600/50 h-12 px-4 text-white placeholder:text-zinc-600 outline-none transition-all duration-300"
              placeholder="Asunto del email"
            />
          </div>
          
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="font-mono text-xs tracking-wider uppercase text-zinc-500">Contenido HTML</label>
              <button
                onClick={() => setShowPreview(!showPreview)}
                data-testid="toggle-preview-btn"
                className="text-xs text-diavel-red hover:text-red-400 flex items-center gap-1 transition-colors"
              >
                <Eye className="w-3 h-3" /> {showPreview ? 'Editar' : 'Vista previa'}
              </button>
            </div>
            
            {showPreview ? (
              <div
                className="bg-white p-4 min-h-[400px] overflow-auto border border-white/10"
                dangerouslySetInnerHTML={{ __html: editing.html }}
                data-testid="email-preview"
              />
            ) : (
              <textarea
                data-testid="email-html-input"
                value={editing.html}
                onChange={e => setEditing(p => ({ ...p, html: e.target.value }))}
                className="w-full bg-zinc-900/50 border border-white/10 focus:border-red-600 focus:ring-1 focus:ring-red-600/50 px-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-300 font-mono text-xs min-h-[400px] resize-y"
                placeholder="Contenido HTML del email"
              />
            )}
          </div>

          <div className="bg-zinc-900/50 border border-white/5 p-4">
            <p className="font-mono text-xs tracking-wider uppercase text-zinc-500 mb-2">Variables disponibles:</p>
            <div className="flex flex-wrap gap-2">
              {['{{nombre}}', '{{fecha_expiracion}}', '{{dias_restantes}}', '{{precio}}', '{{app_url}}'].map(v => (
                <span key={v} className="font-mono text-xs bg-zinc-800 text-diavel-red px-2 py-1 border border-white/5">{v}</span>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3 pt-2">
            <button onClick={handleSave} disabled={saving} data-testid="save-template-btn"
              className="bg-diavel-red text-white font-bold uppercase tracking-wider py-3 px-6 hover:bg-red-700 transition-all duration-300 disabled:opacity-50 flex items-center gap-2 text-sm">
              <Save className="w-4 h-4" /> {saving ? 'Guardando...' : 'Guardar'}
            </button>
            <button onClick={handleSendTest} disabled={sendingTest} data-testid="send-test-btn"
              className="bg-zinc-900 text-white border border-white/10 font-bold uppercase tracking-wider py-3 px-6 hover:border-white/30 transition-all duration-300 disabled:opacity-50 flex items-center gap-2 text-sm">
              <Send className="w-4 h-4" /> {sendingTest ? 'Enviando...' : 'Enviar prueba'}
            </button>
            <button onClick={handleReset} data-testid="reset-template-btn"
              className="bg-zinc-900 text-zinc-500 border border-white/10 font-bold uppercase tracking-wider py-3 px-6 hover:text-white hover:border-white/30 transition-all duration-300 flex items-center gap-2 text-sm">
              <RotateCcw className="w-4 h-4" /> Restaurar
            </button>
          </div>

          {/* Message */}
          {message && (
            <div className={`flex items-center gap-2 p-3 ${message.type === 'success' ? 'bg-green-900/20 border border-green-600/30 text-green-400' : 'bg-red-900/20 border border-red-600/30 text-red-400'}`}>
              {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
              <span className="text-sm">{message.text}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
